<template>
  <AIVDialog
    v-model="isVisible"
    :title="isOnBreak ? 'End Break' : 'Start Break'"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal break-modal"
  >
    <div class="break-modal-content">
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p><strong>Date:</strong> {{ formatDate(shift?.date) }}</p>
        <p>
          <strong>Scheduled Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduled_start || shift?.startTime,
              shift?.scheduled_end || shift?.endTime,
              shift?.date,
            )
          }}
        </p>
        <p>
          <strong>Payer:</strong>
          {{ shift?.payer || shift?.originalData?.payer || "Not specified" }}
        </p>
        <p v-if="shift?.payer !== 'Non Service'"><strong>Client:</strong> {{ formatClient(shift) }}</p>
        <p><strong>Caregiver:</strong> {{ formatCaregiver(shift) }}</p>
        <p>
          <strong>Clock In Time:</strong>
          {{
            formatClockTime(
              shift?.clock_in ||
                shift?.clockIn ||
                shift?.originalData?.clockIn ||
                shift?.originalData?.clock_in ||
                shift?.actualStartTime,
            )
          }}
        </p>

        <!-- Show break start time if currently on break -->
        <p v-if="isOnBreak && currentBreak">
          <strong>Break Started:</strong>
          {{ formatClockTime(currentBreak.break_start) }}
        </p>
      </div>

      <div class="break-confirmation-message">
        <p v-if="!isOnBreak">
          <i class="fa-solid fa-pause"></i>
          You are about to start your break. Your break time will be recorded.
        </p>
        <p v-else>
          <i class="fa-solid fa-play"></i>
          You are about to end your break. The break duration will be calculated and recorded.
        </p>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          :type="isOnBreak ? 'success' : 'warning'"
          :class="isOnBreak ? 'end-break-btn' : 'start-break-btn'"
          @click="confirmBreakAction"
          :loading="processingBreak"
        >
          <i :class="isOnBreak ? 'fa-solid fa-play' : 'fa-solid fa-pause'"></i>
          {{ isOnBreak ? 'End Break' : 'Start Break' }}
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";

export default {
  name: "StartBreakModal",
  components: { AIVDialog },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shift: {
      type: Object,
      default: () => ({})
    },
    userId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      processingBreak: false,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },

    isOnBreak() {
      // Use the isOnBreak flag from the API response
      if (this.shift?.isOnBreak !== undefined) {
        return this.shift.isOnBreak;
      }

      // Fallback: Check if there's an active break in the breaks array
      if (this.shift?.breaks && this.shift.breaks.length > 0) {
        return this.shift.breaks.some(b => b.break_start && !b.break_end);
      }

      // Legacy fallback: Check if there's an active break (break_start exists but break_end doesn't)
      return this.shift?.break_start && !this.shift?.break_end;
    },

    currentBreak() {
      // Get the current active break if exists
      if (this.shift?.breaks && this.shift.breaks.length > 0) {
        return this.shift.breaks.find(b => b.break_start && !b.break_end);
      }
      return null;
    }
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      // Handle date string in YYYY-MM-DD format as local date
      // to avoid timezone conversion issues
      let date;
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Parse as local date by adding time component
        date = new Date(dateString + 'T00:00:00');
      } else {
        // For other formats, use default parsing
        date = new Date(dateString);
      }
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatTimeRange(startTime, endTime, date) {
      if (!startTime || !endTime) return "N/A";

      const formatTime = (timeStr, dateStr) => {
        // Check if it's already a full ISO datetime string
        if (timeStr.includes('T')) {
          const utcDate = new Date(timeStr);
          if (!isNaN(utcDate.getTime())) {
            return utcDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          }
        }

        // Handle time-only format (HH:MM:SS)
        if (timeStr && dateStr) {
          const utcDateTime = `${dateStr.split('T')[0]}T${timeStr}Z`;
          const utcDate = new Date(utcDateTime);
          if (!isNaN(utcDate.getTime())) {
            return utcDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          }
        }

        // Fallback to simple parsing
        const [hour, minute] = timeStr.split(':');
        const hourNum = parseInt(hour, 10);
        const period = hourNum >= 12 ? 'PM' : 'AM';
        const displayHour = hourNum % 12 || 12;
        return `${displayHour}:${minute} ${period}`;
      };

      const start = formatTime(startTime, date);
      const end = formatTime(endTime, date);
      return `${start} - ${end}`;
    },

    formatClockTime(dateTime) {
      if (!dateTime) return "N/A";
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "Invalid Time";

      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatClient(shift) {
      if (!shift) return "N/A";

      // Check for clients array
      if (shift.clients && shift.clients.length > 0) {
        const client = shift.clients[0];
        if (client.name) {
          return client.name;
        }
        // Build name from first_name and last_name
        const firstName = client.first_name || '';
        const lastName = client.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || "N/A";
      }

      // Check originalData
      if (shift.originalData?.clients && shift.originalData.clients.length > 0) {
        const client = shift.originalData.clients[0];
        if (client.name) {
          return client.name;
        }
        const firstName = client.first_name || '';
        const lastName = client.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || "N/A";
      }

      // Check for client string or object
      const client = shift.client || shift.originalData?.client;
      if (client) {
        return typeof client === 'string' ? client : client.name || "N/A";
      }

      const clientName = shift.client_name || shift.originalData?.client_name;
      return clientName || "N/A";
    },

    formatCaregiver(shift) {
      if (!shift) return "N/A";

      // Check for caregivers array
      const caregivers = shift.caregivers || shift.originalData?.caregivers;
      if (caregivers && caregivers.length > 0) {
        const caregiver = caregivers[0];
        if (caregiver.name) {
          return caregiver.name;
        }
        // Build name from first_name and last_name
        const firstName = caregiver.first_name || '';
        const lastName = caregiver.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || "N/A";
      }

      const caregiverName = shift.caregiver_name || shift.originalData?.caregiver_name;
      return caregiverName || "N/A";
    },

    async confirmBreakAction() {
      if (!this.shift?.id) {
        this.$notifyError("Invalid shift data");
        return;
      }

      this.processingBreak = true;

      try {
        let response;
        if (this.isOnBreak) {
          // End the break
          response = await shiftsService.endBreak(this.shift.id);
          const breakDuration = response.break?.durationMinutes || 0;
          this.$notifySuccess(`Break ended! Duration: ${breakDuration} minutes`);
        } else {
          // Start the break
          response = await shiftsService.startBreak(this.shift.id);
          this.$notifySuccess("Break started successfully!");
        }

        this.$emit("break-action-success", response);
        this.isVisible = false;
      } catch (error) {
        const errorMsg = error.response?.data?.error ||
                        error.response?.data?.message ||
                        `Failed to ${this.isOnBreak ? 'end' : 'start'} break`;
        this.$notifyError(errorMsg);
      } finally {
        this.processingBreak = false;
      }
    },

    handleCancel() {
      this.isVisible = false;
    }
  }
};
</script>

<style scoped>
.break-modal-content {
  max-width: 700px;
  min-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.break-modal-content .mb-3 {
  margin-bottom: 1.5rem;
}

.break-modal-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.break-modal-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.break-modal-content strong {
  color: #fff;
  font-weight: 600;
}

.text-center {
  text-align: center;
}

.break-confirmation-message {
  background-color: rgba(23, 162, 184, 0.1);
  border-left: 4px solid rgba(23, 162, 184, 0.8);
  padding: 1rem;
  margin-top: 1.5rem;
  border-radius: 4px;
}

.break-confirmation-message p {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.break-confirmation-message i {
  font-size: 1.2rem;
  color: rgba(23, 162, 184, 1);
}

.start-break-btn {
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.8), rgba(23, 162, 184, 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}

.start-break-btn:hover {
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.9), rgba(23, 162, 184, 0.7)) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.end-break-btn {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.7), rgba(76, 175, 80, 0.5)) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}

.end-break-btn:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.8), rgba(76, 175, 80, 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .break-modal-content {
    min-width: auto !important;
    padding: 0 1rem;
  }

  .break-confirmation-message {
    font-size: 0.9rem;
  }
}
</style>
